import { type AxiosError, type AxiosResponse, isAxiosError } from "axios"
import { ElLoading, ElMessage, ElMessageBox } from "element-plus"
import { onMounted, ref, watchEffect } from "vue"

import { useUserStore } from "@/stores/user"

let is401Handling = false

// 重設 401 處理旗標，在使用者重新登入後呼叫
export function reset401Flag() {
    is401Handling = false
}

export interface UseRequestOptions<T> {
    // 是否在元件 mounted 時立即執行請求
    immediate?: boolean
    // 轉換 response data 的函式，回傳值會同時更新 data 與 response.data
    format?: ((data: unknown) => T) | null
    // 請求失敗時是否顯示錯誤 UI
    showError?: boolean
    // 請求成功時是否顯示 ElMessage toast
    showSuccess?: boolean
    // 請求進行中是否顯示全頁 ElLoading 遮罩
    showLoading?: boolean
    // showError: true 且 isErrorUIMessage: false 時顯示的錯誤文字
    errorMessage?: string
    // showSuccess: true 時顯示的成功文字
    successMessage?: string
    // 請求失敗時，在內部處理完錯誤後是否繼續向上拋出。呼叫端需自行 try/catch 時設為 true。
    isThrowError?: boolean
    // showError: true 時的錯誤顯示方式。
    isErrorUIMessage?: boolean
    // 初始參數
    initialConfig?: Record<string, unknown>
    // 傳給 service 函式的參數
    args?: unknown[]
}

interface ExecuteOptions {
    // 動態覆蓋本次請求的成功訊息，優先於 `successMessage`。
    dynamicSuccessMessage?: string
}

function showErrorMessage(message: string): void {
    ElMessage({ message, type: "error" })
}

function useRequest<TArgs extends unknown[], T = unknown>(
    service: (...args: TArgs) => Promise<AxiosResponse<T>>,
    {
        immediate = true,
        format = null,
        showError = false,
        showSuccess = false,
        showLoading = false,
        errorMessage = "",
        successMessage = "",
        isThrowError = false,
        initialConfig = {},
        isErrorUIMessage = false,
        args = [],
    }: UseRequestOptions<T> = {}
) {
    const data = ref<T | null>(null)
    const isLoading = ref(false)
    const status = ref<"idle" | "success" | "loading" | "error">("idle")
    const response = ref<AxiosResponse<T> | null>(null)
    const error = ref<AxiosError | Error | null>(null)
    const loadingInstance = ref<ReturnType<typeof ElLoading.service> | null>(null)

    watchEffect(() => {
        if (!showLoading) return

        if (isLoading.value) {
            loadingInstance.value = ElLoading.service({
                lock: true,
                text: "Loading",
            })
        } else {
            loadingInstance.value?.close()
        }
    })

    function showErrorDialog(message: string): void {
        ElMessageBox.alert(errorMessage || message, "錯誤", {
            confirmButtonText: "確認",
            type: "error",
            customClass: "[&>*:first-child>*:first-child]:!justify-start [&_*]:text-left [&>*:last-child]:!justify-end",
            center: true,
        })
    }

    function handle401(responseError: AxiosError): void {
        if (is401Handling) return
        is401Handling = true
        if (showError) {
            ElMessage({
                message: `${responseError.message} (status: ${responseError.status})`,
                type: "error",
            })
        }
        useUserStore().logout()
    }

    function handleError(responseError: unknown): void {
        status.value = "error"

        if (!isAxiosError(responseError)) {
            const err = responseError instanceof Error ? responseError : new Error(String(responseError))
            error.value = err
            console.error(err)
            if (isThrowError) throw err
            return
        }

        error.value = responseError

        if (responseError.status === 401) {
            handle401(responseError)
            if (isThrowError) throw responseError
            return
        }

        if (showError) {
            const message = `${responseError.message} (status: ${responseError.status})`
            if (isErrorUIMessage) {
                showErrorMessage(message)
            } else {
                showErrorDialog(message)
            }
        }

        if (isThrowError) throw responseError
    }

    async function execute(...executeArguments: [...TArgs, ExecuteOptions?]) {
        const isOptions = (arg: unknown): arg is ExecuteOptions =>
            arg !== null && typeof arg === "object" && "dynamicSuccessMessage" in arg

        const argsArray: unknown[] = [...executeArguments]
        let options: ExecuteOptions = {}
        const lastArg = argsArray.at(-1)
        if (isOptions(lastArg)) {
            argsArray.pop()
            options = lastArg
        }

        const lastRemainingArg = argsArray.at(-1)
        const isConfigLike =
            typeof lastRemainingArg === "object" && lastRemainingArg !== null && !Array.isArray(lastRemainingArg)

        let executeArgs: TArgs
        if (isConfigLike) {
            const mergedConfig = { ...initialConfig, ...(lastRemainingArg as Record<string, unknown>) }
            executeArgs = [...argsArray.slice(0, -1), mergedConfig] as TArgs
        } else {
            executeArgs = [...argsArray, { ...initialConfig }] as TArgs
        }

        try {
            error.value = null
            status.value = "loading"
            isLoading.value = true

            const axiosResponse = await service(...executeArgs)
            const formatData = format ? format(axiosResponse.data) : axiosResponse.data
            axiosResponse.data = formatData
            data.value = formatData
            response.value = axiosResponse

            status.value = "success"

            if (showSuccess) {
                ElMessage({
                    message: options.dynamicSuccessMessage || successMessage,
                    type: "success",
                })
            }
        } catch (responseError) {
            handleError(responseError)
        } finally {
            isLoading.value = false
        }

        return response
    }

    if (immediate) {
        onMounted(() => {
            void execute(...(args as TArgs), initialConfig as never)
        })
    }

    return { data, isLoading, status, response, error, execute }
}

export default useRequest
